import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { faBookmark } from '@fortawesome/pro-duotone-svg-icons'

import AppLayout from '../../components/app-layout/AppLayout'
import Button from '../../components/button/Button'
import Icon from '../../components/icon/Icon'
import CompareModal from '../../components/compare-modal/CompareModal'
import DiscoverListingCard from '../../components/discover-listing-card/DiscoverListingCard'
import ImportListingModal from '../../components/import-listing-modal/ImportListingModal'
import ImportedListingCard from '../../components/imported-listing-card/ImportedListingCard'
import ListingsQuestionBar from '../../components/listings-question-bar/ListingsQuestionBar'
import ListingsToolbar from '../../components/listings-toolbar/ListingsToolbar'
import { applyListingsToolbar } from '../../components/listings-toolbar/helpers'
import {
  ListingsSortOrder,
  ListingsTagFilter,
} from '../../components/listings-toolbar/types'
import UndoToast from '../../components/undo-toast/UndoToast'
import { useAuth } from '../../_hooks/use-auth'
import { useCompareSelection } from '../../_hooks/use-compare-selection'
import { useListingDecisions } from '../../_hooks/use-listing-decisions'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useUndoToast } from '../../_hooks/use-undo-toast'
import { computeMatchScore } from '../../components/match-score-badge/helpers'
import { fetchDiscoverListingsByIds } from '../../_data/discover'
import { getEmbedHostname } from '../../_utilities/embed-context'
import { normalizeHostname } from '../../_utilities/listing-host'
import { DiscoverListing } from '../../_types/quickpass-discover'
import {
  PageStack,
  Header,
  HeaderRow,
  HeaderText,
  PageTitle,
  PageSubtitle,
  ScopeToggle,
  ScopeButton,
  SectionTitle,
  ListingsGrid,
  EmptyCard,
  EmptyEmoji,
  EmptyTitle,
  EmptyBody,
  EmptyActions,
  CompareBar,
  CompareBarText,
  CompareBarPrimary,
  CompareBarSecondary,
} from './styled'
import {
  savedListingsContainerMotion,
  savedListingsItemMotion,
  filterImportedListingsByHostname,
} from './helpers'
import { SavedListingsScope } from './types'

/**
 * Renter's saved-listings index. Combines two streams:
 *   - Cross-site saved listings (from QC-powered sites — Slice 5+)
 *   - URL-imported listings (paste-a-URL flow with daily rescrape)
 *
 * @returns {JSX.Element}
 */
const SavedListingsPage = (): JSX.Element => {
  const { profile, patchProfile, unsaveListingFromProfile } =
    useQuickPassProfile()
  const { quickPassUser } = useAuth()
  const { listings: importedListings, refreshListing } = useImportedListings()
  const { tags } = useListingDecisions()
  const undoToast = useUndoToast()
  const navigate = useNavigate()

  const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false)
  const [refreshingListingId, setRefreshingListingId] = useState<string | null>(
    null,
  )
  const [tagFilter, setTagFilter] = useState<ListingsTagFilter>('all')
  const [sortOrder, setSortOrder] = useState<ListingsSortOrder>('newest')
  const compareSelection = useCompareSelection()
  const [isCompareOpen, setIsCompareOpen] = useState<boolean>(false)
  const [networkListings, setNetworkListings] = useState<DiscoverListing[]>([])
  const [isHydratingNetwork, setIsHydratingNetwork] = useState<boolean>(false)

  const embedHostname = useMemo<string | null>(() => getEmbedHostname(), [])
  const [scope, setScope] = useState<SavedListingsScope>(
    embedHostname !== null ? 'site' : 'all',
  )

  const archivedIds = useMemo<Set<string>>(
    () => new Set(profile?.archivedListingIds ?? []),
    [profile],
  )

  const unarchivedListings = useMemo(
    () => importedListings.filter(entry => archivedIds.has(entry.id) === false),
    [importedListings, archivedIds],
  )

  const isSiteScoped = scope === 'site' && embedHostname !== null

  const scopedImportedListings = useMemo(() => {
    if (isSiteScoped === false || embedHostname === null) {
      return unarchivedListings
    }

    return filterImportedListingsByHostname(unarchivedListings, embedHostname)
  }, [unarchivedListings, isSiteScoped, embedHostname])

  const visibleImportedListings = useMemo(
    () =>
      applyListingsToolbar(
        scopedImportedListings,
        tags,
        profile?.unitPreferences ?? null,
        tagFilter,
        sortOrder,
      ),
    [scopedImportedListings, tags, profile, tagFilter, sortOrder],
  )

  const savedListingIds = useMemo<string[]>(
    () => profile?.savedListingIds ?? [],
    [profile],
  )
  const importedIdSet = useMemo<Set<string>>(
    () => new Set(importedListings.map(entry => entry.id)),
    [importedListings],
  )
  const networkOnlySavedIds = useMemo<string[]>(
    () => savedListingIds.filter(id => importedIdSet.has(id) === false),
    [savedListingIds, importedIdSet],
  )
  const savedListingHosts = useMemo<Record<string, string>>(
    () => profile?.savedListingHosts ?? {},
    [profile],
  )
  const effectiveNetworkSavedIds = useMemo<string[]>(() => {
    if (isSiteScoped === false || embedHostname === null) {
      return networkOnlySavedIds
    }

    return networkOnlySavedIds.filter(
      id => normalizeHostname(savedListingHosts[id] ?? '') === embedHostname,
    )
  }, [networkOnlySavedIds, savedListingHosts, isSiteScoped, embedHostname])
  const showNetworkSection = effectiveNetworkSavedIds.length > 0
  const hasNetwork = networkListings.length > 0
  const hasImported = scopedImportedListings.length > 0
  const hasAnything =
    hasImported === true || effectiveNetworkSavedIds.length > 0

  useEffect(() => {
    if (effectiveNetworkSavedIds.length === 0) {
      setNetworkListings([])
      return
    }

    let isMounted = true
    setIsHydratingNetwork(true)

    void fetchDiscoverListingsByIds({
      ids: effectiveNetworkSavedIds,
      quickPassUserId: quickPassUser?.id ?? null,
    }).then(hydrated => {
      if (isMounted === false) return
      setNetworkListings(hydrated)
      setIsHydratingNetwork(false)
    })

    return () => {
      isMounted = false
    }
  }, [effectiveNetworkSavedIds, quickPassUser?.id])

  const handleNetworkSaveToggle = useCallback(
    async (listingId: string): Promise<void> => {
      try {
        await unsaveListingFromProfile(listingId)
      } catch (error) {
        console.error('[saved-listings] unsave failed', error)
      }
    },
    [unsaveListingFromProfile],
  )

  const handleRemoveImported = async (listingId: string): Promise<void> => {
    if (profile === null) {
      return
    }

    const listingTitle =
      importedListings.find(entry => entry.id === listingId)?.title ??
      'this listing'
    const previousArchived = profile.archivedListingIds ?? []
    const nextArchived = [...previousArchived, listingId]

    try {
      await patchProfile({ archivedListingIds: nextArchived })
      undoToast.show(`Archived "${listingTitle}".`, () => {
        void patchProfile({ archivedListingIds: previousArchived })
      })
    } catch (error) {
      console.error('Failed to archive imported listing', error)
    }
  }

  const handleRefreshImported = async (listingId: string): Promise<void> => {
    setRefreshingListingId(listingId)
    try {
      await refreshListing(listingId)
    } catch (error) {
      console.error('Failed to refresh imported listing', error)
    } finally {
      setRefreshingListingId(null)
    }
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack
        variants={savedListingsContainerMotion}
        initial="hidden"
        animate="visible"
      >
        <Header variants={savedListingsItemMotion}>
          <HeaderRow>
            <HeaderText>
              <PageTitle>
                {isSiteScoped === true
                  ? `Saved on ${embedHostname}`
                  : 'Saved listings'}
              </PageTitle>
              <PageSubtitle>
                {isSiteScoped === true
                  ? `The places you've saved while browsing ${embedHostname}.`
                  : hasAnything === true
                    ? "Everything you've bookmarked across the network and imported by URL."
                    : 'Listings you save here are tracked daily for price + availability changes.'}
              </PageSubtitle>
            </HeaderText>
            <Button onClick={() => setIsImportModalOpen(true)}>
              + Import a place
            </Button>
          </HeaderRow>

          {embedHostname !== null ? (
            <ScopeToggle role="tablist" aria-label="Saved listings scope">
              <ScopeButton
                type="button"
                role="tab"
                aria-selected={scope === 'site'}
                isActive={scope === 'site'}
                onClick={() => setScope('site')}
              >
                On {embedHostname}
              </ScopeButton>
              <ScopeButton
                type="button"
                role="tab"
                aria-selected={scope === 'all'}
                isActive={scope === 'all'}
                onClick={() => setScope('all')}
              >
                All saved
              </ScopeButton>
            </ScopeToggle>
          ) : null}
        </Header>

        {hasImported === true ? (
          <motion.div variants={savedListingsItemMotion}>
            <ListingsQuestionBar
              listings={scopedImportedListings}
              preferences={profile?.unitPreferences ?? null}
              onMatchedListingClick={listingId =>
                navigate(`/imported-listings/${listingId}`)
              }
            />
            <SectionTitle>Imported listings</SectionTitle>
            <ListingsToolbar
              tagFilter={tagFilter}
              sortOrder={sortOrder}
              onTagFilterChange={setTagFilter}
              onSortOrderChange={setSortOrder}
              totalCount={scopedImportedListings.length}
              visibleCount={visibleImportedListings.length}
            />
            <ListingsGrid>
              {visibleImportedListings.map(listing => {
                const breakdown = computeMatchScore(
                  listing,
                  profile?.unitPreferences ?? null,
                )
                return (
                  <ImportedListingCard
                    key={listing.id}
                    listing={listing}
                    onRemove={handleRemoveImported}
                    onRefresh={handleRefreshImported}
                    isRefreshing={refreshingListingId === listing.id}
                    matchScore={
                      breakdown.hasPreferences === true ? breakdown.score : null
                    }
                    isCompareSelected={compareSelection.isSelected(listing.id)}
                    onToggleCompare={compareSelection.toggleSelected}
                  />
                )
              })}
            </ListingsGrid>
          </motion.div>
        ) : null}

        {showNetworkSection === true ? (
          <motion.div variants={savedListingsItemMotion}>
            <SectionTitle>From the QuickPass network</SectionTitle>
            {hasNetwork === true ? (
              <ListingsGrid>
                {networkListings.map(listing => (
                  <DiscoverListingCard
                    key={listing.id}
                    listing={listing}
                    isSaved
                    onSaveToggle={handleNetworkSaveToggle}
                  />
                ))}
              </ListingsGrid>
            ) : isHydratingNetwork === true ? (
              <EmptyBody>Loading your saved places…</EmptyBody>
            ) : (
              <EmptyBody>
                Your saved network places aren&rsquo;t loading right now — they
                may have been pulled by the property manager. Try refreshing in
                a moment.
              </EmptyBody>
            )}
          </motion.div>
        ) : null}

        {hasAnything === false ? (
          <motion.div variants={savedListingsItemMotion}>
            <EmptyCard>
              <EmptyEmoji aria-hidden="true">
                <Icon icon={faBookmark} label="Bookmark" />
              </EmptyEmoji>
              {isSiteScoped === true ? (
                <>
                  <EmptyTitle>Nothing saved on {embedHostname} yet</EmptyTitle>
                  <EmptyBody>
                    Save a place while browsing {embedHostname} and it&apos;ll
                    show up right here. Saved listings from other sites are
                    under &ldquo;All saved&rdquo;.
                  </EmptyBody>
                  <EmptyActions>
                    <Button variant="secondary" onClick={() => setScope('all')}>
                      View all saved
                    </Button>
                    <Button onClick={() => setIsImportModalOpen(true)}>
                      + Import a place
                    </Button>
                  </EmptyActions>
                </>
              ) : (
                <>
                  <EmptyTitle>Nothing saved yet</EmptyTitle>
                  <EmptyBody>
                    Paste any rental listing URL — rentals.ca, Kijiji, anywhere
                    — and we&apos;ll grab the details and watch it daily for
                    price + availability changes. Cross-site saving from
                    QuickPass-powered sites lights up in Slice 5.
                  </EmptyBody>
                  <EmptyActions>
                    <Button onClick={() => setIsImportModalOpen(true)}>
                      + Import a place
                    </Button>
                  </EmptyActions>
                </>
              )}
            </EmptyCard>
          </motion.div>
        ) : null}
      </PageStack>

      <ImportListingModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
      />

      {compareSelection.selectedIds.length > 0 && isCompareOpen === false ? (
        <CompareBar>
          <CompareBarText>
            {compareSelection.selectedIds.length === 1
              ? 'Pick one more to compare'
              : 'Two places ready to compare'}
          </CompareBarText>
          <CompareBarPrimary
            type="button"
            onClick={() => setIsCompareOpen(true)}
            disabled={compareSelection.canCompare === false}
          >
            Compare
          </CompareBarPrimary>
          <CompareBarSecondary
            type="button"
            onClick={() => compareSelection.clearSelection()}
          >
            Clear
          </CompareBarSecondary>
        </CompareBar>
      ) : null}

      {isCompareOpen === true ? (
        <CompareModal
          alpha={
            importedListings.find(
              entry => entry.id === compareSelection.selectedIds[0],
            ) ?? null
          }
          beta={
            importedListings.find(
              entry => entry.id === compareSelection.selectedIds[1],
            ) ?? null
          }
          onClose={() => setIsCompareOpen(false)}
        />
      ) : null}

      <UndoToast controller={undoToast} />
    </AppLayout>
  )
}

export default SavedListingsPage
